import React, { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { isMacLikeKeyboard } from '../../../../../utils'

interface IKeyboardShortcutsModalProps {
    onClose: () => void
}

interface IShortcut {
    keys: string[]
    description: string
    sep?: '/' | '+'
}

interface ISection {
    title: string
    shortcuts: IShortcut[]
}

const LEFT_SECTIONS: ISection[] = [
    {
        title: 'Navigation',
        shortcuts: [
            { keys: ['K'], description: 'Next conversation' },
            { keys: ['J'], description: 'Previous conversation' },
            { keys: ['N'], description: 'Next + AI generate' },
            { keys: ['B'], description: 'Prev + AI generate' },
        ],
    },
    {
        title: 'Conversation',
        shortcuts: [
            { keys: ['G'], description: 'AI generate' },
            { keys: ['E'], description: 'Archive / Unarchive' },
            { keys: ['S'], description: 'Star / Unstar' },
            { keys: ['P'], description: 'Pin / Unpin' },
            { keys: ['I'], description: 'Mark as Read / Unread' },
            { keys: ['Z'], description: 'Snooze / Unsnooze' },
            { keys: ['L'], description: 'Open LinkedIn profile' },
            { keys: ['F'], description: 'Open LinkedIn activity' },
        ],
    },
]

const RIGHT_SECTIONS: ISection[] = [
    {
        title: 'Compose',
        shortcuts: [
            { keys: ['C'], description: 'New message' },
            { keys: ['R'], description: 'Focus reply box' },
            { keys: ['T'], description: 'Open templates' },
            { keys: ['#'], description: 'Apply tags' },
        ],
    },
    {
        title: 'Message input',
        shortcuts: [
            { keys: ['Ctrl', 'Shift', 'G'], description: 'AI generate', sep: '+' },
            { keys: ['Ctrl', 'Shift', 'T'], description: 'Templates', sep: '+' },
            { keys: ['Ctrl', 'Shift', 'A'], description: 'Tags', sep: '+' },
            { keys: ['Ctrl', 'Shift', 'H'], description: 'Attach file', sep: '+' },
            { keys: ['Ctrl', 'Shift', 'E'], description: 'Emoji picker', sep: '+' },
        ],
    },
    {
        title: 'General',
        shortcuts: [
            { keys: ['?'], description: 'Show this panel' },
            { keys: ['Esc'], description: 'Close panel' },
        ],
    },
]

const KeyboardShortcutsModal: React.FC<IKeyboardShortcutsModalProps> = ({ onClose }) => {
    const isMac = isMacLikeKeyboard()

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [onClose])

    const renderSection = (section: ISection) => (
        <Section key={section.title}>
            <SectionTitle>{section.title}</SectionTitle>
            {section.shortcuts.map(({ keys, description, sep = '/' }, idx) => (
                <Row key={`${section.title}-${idx}`}>
                    <KeysGroup>
                        {keys.map((k, i) => (
                            <React.Fragment key={`${description}-${i}-${k}`}>
                                <Kbd>
                                    {k === 'Ctrl' ? (
                                        <>
                                            Ctrl
                                            {isMac && <CtrlMacHint>(⌃)</CtrlMacHint>}
                                        </>
                                    ) : (
                                        k
                                    )}
                                </Kbd>
                                {i < keys.length - 1 && <Sep sep={sep}>{sep}</Sep>}
                            </React.Fragment>
                        ))}
                    </KeysGroup>
                    <Desc>{description}</Desc>
                </Row>
            ))}
        </Section>
    )

    return (
        <Overlay onClick={onClose}>
            <Panel onClick={e => e.stopPropagation()}>
                <Header>
                    <HeaderLeft>
                        <KeyboardIcon />
                        <Title>Keyboard Shortcuts</Title>
                    </HeaderLeft>
                    <CloseBtn onClick={onClose} aria-label="Close">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </CloseBtn>
                </Header>
                <Body>
                    <Col>{LEFT_SECTIONS.map(renderSection)}</Col>
                    <ColDivider />
                    <Col>{RIGHT_SECTIONS.map(renderSection)}</Col>
                </Body>
                <Footer>
                    Press <Kbd style={{ display: 'inline-flex', marginInline: 4 }}>?</Kbd> anytime to toggle this panel
                </Footer>
            </Panel>
        </Overlay>
    )
}

const KeyboardIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5964ff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="6" width="20" height="13" rx="2" />
        <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8M6 10v.01M10 10v.01M14 10v.01M18 10v.01" />
    </svg>
)

const fadeSlide = keyframes`
    from { opacity: 0; transform: translateY(-6px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
`

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9000;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 76px 20px 0 0;
    pointer-events: all;
`
const Panel = styled.div`
    width: 560px;
    background: ${props => props.theme.secondaryColor || '#fff'};
    border: 1px solid ${props => props.theme.quinaryColor || '#e2e8f0'};
    border-radius: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.13), 0 2px 8px rgba(0,0,0,0.06);
    overflow: hidden;
    animation: ${fadeSlide} 0.18s ease;
`
const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 13px;
    background: linear-gradient(135deg, #f8f9ff 0%, #f4f6ff 100%);
    border-bottom: 1px solid ${props => props.theme.quinaryColor || '#e2e8f0'};
`
const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`
const Title = styled.h3`
    font-size: 13px;
    font-weight: 700;
    color: ${props => props.theme.primaryColor};
    margin: 0;
    letter-spacing: -0.1px;
`
const CloseBtn = styled.button`
    background: none;
    border: none;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8c96ad;
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
    &:hover {
        background: #eef0f6;
        color: #5964ff;
    }
`
const Body = styled.div`
    padding: 16px 18px 14px;
    display: flex;
    gap: 0;
`
const Col = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
`
const ColDivider = styled.div`
    width: 1px;
    background: ${props => props.theme.quinaryColor || '#e2e8f0'};
    margin: 0 18px;
    align-self: stretch;
`
const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`
const SectionTitle = styled.div`
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #5964ff;
    opacity: 0.7;
    margin-bottom: 4px;
    padding-bottom: 5px;
    border-bottom: 1px solid #eef0ff;
`
const Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 2px 0;
    border-radius: 5px;
    transition: background 0.1s;
    &:hover { background: #f8f9ff; }
`
const KeysGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    flex-shrink: 0;
`
const CtrlMacHint = styled.span`
    margin-left: 3px;
    font-size: 9px;
    font-weight: 600;
    color: #6b7788;
    opacity: 0.95;
`
const Kbd = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 6px;
    background: linear-gradient(180deg, #ffffff 0%, #f3f5f9 100%);
    border: 1px solid #cdd5e0;
    border-bottom: 2px solid #b8c1cf;
    border-radius: 5px;
    font-family: 'SFMono-Regular', ui-monospace, Consolas, monospace;
    font-size: 10px;
    font-weight: 600;
    color: #3d4a5c;
    white-space: nowrap;
    box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset;
`
const Sep = styled.span<{ sep?: string }>`
    font-size: 10px;
    font-weight: ${props => props.sep === '+' ? '700' : '400'};
    color: ${props => props.sep === '+' ? '#aab4c4' : '#c8d0dc'};
    flex-shrink: 0;
    line-height: 1;
`
const Desc = styled.span`
    font-size: 12px;
    color: #5a6679;
    flex: 1;
    text-align: right;
`
const Footer = styled.div`
    padding: 9px 18px 11px;
    background: #f8f9ff;
    border-top: 1px solid ${props => props.theme.quinaryColor || '#e2e8f0'};
    font-size: 11px;
    color: #9aa4b8;
    display: flex;
    align-items: center;
`

export default KeyboardShortcutsModal
